export class HttpError extends Error {
  readonly status: number;
  readonly body: string;

  constructor(status: number, message: string, body = "") {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.body = body;
  }
}

export function isHttpError(err: unknown): err is HttpError {
  return err instanceof HttpError;
}

type RequestOptions = {
  headers?: Record<string, string>;
  params?: Record<string, string>;
  body?: unknown;
};

function withParams(url: string, params?: Record<string, string>): string {
  if (!params || Object.keys(params).length === 0) return url;
  const u = new URL(url);
  for (const [k, v] of Object.entries(params)) u.searchParams.set(k, v);
  return u.toString();
}

async function request<T>(
  url: string,
  method: string,
  options: RequestOptions = {}
): Promise<T> {
  const { headers = {}, params, body } = options;
  const res = await fetch(withParams(url, params), {
    method,
    headers:
      body !== undefined
        ? { "Content-Type": "application/json", ...headers }
        : headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new HttpError(
      res.status,
      text || res.statusText || `HTTP ${res.status}`,
      text
    );
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export const http = {
  get<T>(url: string, options?: Omit<RequestOptions, "body">) {
    return request<T>(url, "GET", options);
  },
  post<T>(url: string, body?: unknown, options?: Omit<RequestOptions, "body">) {
    return request<T>(url, "POST", { ...options, body });
  },
  put<T>(url: string, body?: unknown, options?: Omit<RequestOptions, "body">) {
    return request<T>(url, "PUT", { ...options, body });
  },
  delete(url: string, options?: Omit<RequestOptions, "body">) {
    return request<void>(url, "DELETE", options);
  },
};